import React, { useState, useEffect, useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';
import "./App.css";
import { BADGE_DEFINITIONS, MEMORY_METHODS, PRACTICE_TYPES, MEMORY_TIPS, TUTORIALS } from './data';

// GA4 event helper
const trackEvent = (name, params) => {
  if (window.gtag) {
    window.gtag('event', name, params);
  }
};

// Sound helper using Web Audio API
const playSound = (type) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    if (type === 'success') {
      oscillator.type = 'sine';
      oscillator.frequency.value = 800;
      gain.gain.value = 0.3;
      oscillator.start();
      oscillator.frequency.linearRampToValueAtTime(1200, ctx.currentTime + 0.15);
      oscillator.stop(ctx.currentTime + 0.2);
    } else {
      oscillator.type = 'square';
      oscillator.frequency.value = 250;
      gain.gain.value = 0.15;
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.3);
    }
  } catch (e) { /* Audio not available */ }
};

// Uncontrolled input component to bypass React controlled component RTL issues
const UncontrolledTextarea = React.memo(React.forwardRef(({ placeholder, className }, ref) => {
  return (
    <textarea
      ref={ref}
      className={className}
      placeholder={placeholder}
      dir="ltr"
      lang="en"
    />
  );
}));

function App() {
  const [currentScreen, setCurrentScreenRaw] = useState('home');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [progress, setProgress] = useState({});
  const [timeSpent, setTimeSpent] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [itemsHidden, setItemsHidden] = useState(false);
  const [studyTime, setStudyTime] = useState(0);
  const [detailedResults, setDetailedResults] = useState([]);

  // Timed mode state
  const [timedMode, setTimedMode] = useState(false);
  const [countdown, setCountdown] = useState(null);

  // Streak & gamification state
  const [streak, setStreak] = useState({
    currentStreak: 0,
    longestStreak: 0,
    lastPracticeDate: null,
    badges: [],
    techniquesUsed: []
  });
  const [toast, setToast] = useState(null);

  const inputRef = useRef(null);
  const toastTimerRef = useRef(null);

  // Wrap setCurrentScreen with GA4 tracking for technique selection
  const setCurrentScreen = (screen) => {
    if (screen !== 'home' && screen !== 'practice' && MEMORY_METHODS[screen]) {
      trackEvent('technique_selected', { technique_name: MEMORY_METHODS[screen].name });
    }
    setCurrentScreenRaw(screen);
  };

  // Load progress from localStorage
  useEffect(() => {
    const savedProgress = localStorage.getItem('memoryProgress');
    if (savedProgress) {
      setProgress(JSON.parse(savedProgress));
    }
  }, []);

  // Load streak from localStorage
  useEffect(() => {
    const savedStreak = localStorage.getItem('memoryStreak');
    if (savedStreak) {
      setStreak(JSON.parse(savedStreak));
    }
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // Track if countdown was previously non-null to prevent auto-trigger on reset
  const prevCountdownRef = useRef(null);

  // Auto-hide items when study countdown reaches 0
  useEffect(() => {
    if (countdown === 0 && timedMode && !itemsHidden && !showResults && currentChallenge) {
      // Only trigger if countdown just reached 0 (was > 0 previously)
      if (prevCountdownRef.current !== null && prevCountdownRef.current > 0) {
        hideItemsAndStartTyping();
      }
    }
  }, [countdown, timedMode, itemsHidden, showResults, currentChallenge]);

  // Auto-submit when recall countdown reaches 0
  useEffect(() => {
    if (countdown === 0 && timedMode && itemsHidden && !showResults) {
      // Only trigger if countdown just reached 0 (was > 0 previously)
      if (prevCountdownRef.current !== null && prevCountdownRef.current > 0) {
        submitAnswer();
      }
    }
  }, [countdown, timedMode, itemsHidden, showResults]);

  // Update prev countdown ref on every render
  useEffect(() => {
    prevCountdownRef.current = countdown;
  }, [countdown]);

  // Save progress to localStorage
  const saveProgress = (newProgress) => {
    setProgress(newProgress);
    localStorage.setItem('memoryProgress', JSON.stringify(newProgress));
  };

  // Save streak to localStorage
  const saveStreak = (newStreak) => {
    setStreak(newStreak);
    localStorage.setItem('memoryStreak', JSON.stringify(newStreak));
  };

  // Show toast notification
  const showToast = (message, icon) => {
    setToast({ message, icon });
  };

  // Auto-dismiss toast after 3 seconds
  // Uses toastTimerRef declared at component start
  useEffect(() => {
    if (toast) {
      // Clear any existing timer when toast changes
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
      // Set new timer to hide toast after 3 seconds
      toastTimerRef.current = setTimeout(() => {
        setToast(null);
      }, 3000);
    }

    // Cleanup timer on unmount or when toast is cleared
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, [toast]);

  // Update streak after each practice
  const updateStreak = (method) => {
    const today = new Date().toISOString().split('T')[0];
    const newStreak = { ...streak };

    // Update techniques used
    if (!newStreak.techniquesUsed) newStreak.techniquesUsed = [];
    if (!newStreak.techniquesUsed.includes(method)) {
      newStreak.techniquesUsed = [...newStreak.techniquesUsed, method];
    }

    if (newStreak.lastPracticeDate === today) {
      // Already practiced today, no streak change
      saveStreak(newStreak);
      return newStreak;
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    if (newStreak.lastPracticeDate === yesterdayStr) {
      // Consecutive day
      newStreak.currentStreak = (newStreak.currentStreak || 0) + 1;
    } else {
      // Streak broken or first practice
      newStreak.currentStreak = 1;
    }

    newStreak.longestStreak = Math.max(newStreak.longestStreak || 0, newStreak.currentStreak);
    newStreak.lastPracticeDate = today;

    if (newStreak.currentStreak === 3 || newStreak.currentStreak === 7) {
      trackEvent('streak_achieved', { streak_days: newStreak.currentStreak });
    }

    saveStreak(newStreak);
    return newStreak;
  };

  // Check and award badges
  const checkBadges = (scorePercentage, method, updatedStreak) => {
    const currentBadges = [...(updatedStreak.badges || [])];
    const newBadges = [];

    // First practice
    if (!currentBadges.includes('first_practice')) {
      currentBadges.push('first_practice');
      newBadges.push('first_practice');
    }

    // Perfect score
    if (scorePercentage === 100 && !currentBadges.includes('perfect_score')) {
      currentBadges.push('perfect_score');
      newBadges.push('perfect_score');
    }

    // Streak badges
    if (updatedStreak.currentStreak >= 3 && !currentBadges.includes('streak_3')) {
      currentBadges.push('streak_3');
      newBadges.push('streak_3');
    }
    if (updatedStreak.currentStreak >= 7 && !currentBadges.includes('streak_7')) {
      currentBadges.push('streak_7');
      newBadges.push('streak_7');
    }

    // All techniques
    if (updatedStreak.techniquesUsed.length >= 10 && !currentBadges.includes('all_techniques')) {
      currentBadges.push('all_techniques');
      newBadges.push('all_techniques');
    }

    if (newBadges.length > 0) {
      const finalStreak = { ...updatedStreak, badges: currentBadges };
      saveStreak(finalStreak);
      // Show toast for the first new badge
      const badge = BADGE_DEFINITIONS[newBadges[0]];
      showToast(`Badge earned: ${badge.name}!`, badge.icon);
      newBadges.forEach(b => trackEvent('badge_earned', { badge_id: b }));
    }
  };

  const startPractice = (method, type) => {
    setSelectedMethod(method);
    setSelectedType(type);
    const practiceData = PRACTICE_TYPES[method][type];
    const randomChallenge = practiceData.items[Math.floor(Math.random() * practiceData.items.length)];
    setCurrentChallenge(randomChallenge);
    setCurrentScreenRaw('practice');
    setUserInput('');
    setShowResults(false);
    setItemsHidden(false);
    setStudyTime(0);
    setStartTime(Date.now());
    setDetailedResults([]);

    // Start timed mode countdown for study phase
    if (timedMode) {
      setCountdown(30);
    } else {
      setCountdown(null);
    }

    trackEvent('practice_started', {
      technique_name: MEMORY_METHODS[method].name,
      practice_type: practiceData.name,
      timed_mode: timedMode
    });
  };

  const submitAnswer = () => {
    const inputValue = inputRef.current.value;

    const endTime = Date.now();
    const timeSpentSeconds = Math.round((endTime - startTime) / 1000);
    setTimeSpent(timeSpentSeconds);

    const userAnswers = inputValue.split(',').map(s => s.trim().toLowerCase());
    const correctAnswers = currentChallenge.map(s => s.toLowerCase());

    // Build detailed results
    const results = correctAnswers.map((expected, index) => {
      const userAnswer = userAnswers[index] ? userAnswers[index].trim() : '';
      // Only mark as correct if user provided an answer and it matches
      const isCorrect = userAnswer.length > 0 && expected.toLowerCase().includes(userAnswer.toLowerCase());
      return {
        expected: currentChallenge[index],
        userAnswer: userAnswer,
        isCorrect
      };
    });
    setDetailedResults(results);

    const correctCount = results.filter(r => r.isCorrect).length;
    const scorePercentage = Math.round((correctCount / correctAnswers.length) * 100);
    setScore(scorePercentage);

    setUserInput(inputValue);
    setShowResults(true);
    setCountdown(null);

    // Confetti on perfect score
    if (scorePercentage === 100) {
      try { confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } }); } catch (e) { /* canvas not available in test */ }
      trackEvent('perfect_score', {
        technique_name: MEMORY_METHODS[selectedMethod].name,
        practice_type: PRACTICE_TYPES[selectedMethod][selectedType].name
      });
    }

    // Sound effects
    if (scorePercentage >= 80) {
      playSound('success');
    } else if (scorePercentage < 60) {
      playSound('fail');
    }

    // GA4 event
    trackEvent('answer_submitted', {
      technique_name: MEMORY_METHODS[selectedMethod].name,
      practice_type: PRACTICE_TYPES[selectedMethod][selectedType].name,
      score: scorePercentage,
      answer_time_seconds: timeSpentSeconds
    });

    // Update progress
    const progressKey = `${selectedMethod}_${selectedType}`;
    const newProgress = { ...progress };
    if (!newProgress[progressKey]) {
      newProgress[progressKey] = { attempts: 0, bestScore: 0, totalTime: 0 };
    }
    newProgress[progressKey].attempts++;
    newProgress[progressKey].bestScore = Math.max(newProgress[progressKey].bestScore, scorePercentage);
    newProgress[progressKey].totalTime += timeSpentSeconds;
    saveProgress(newProgress);

    // Update streak and check badges
    const updatedStreak = updateStreak(selectedMethod);
    checkBadges(scorePercentage, selectedMethod, updatedStreak);
  };

  const hideItemsAndStartTyping = () => {
    setItemsHidden(true);
    const studySeconds = Math.round((Date.now() - startTime) / 1000);
    setStudyTime(studySeconds);
    setStartTime(Date.now());

    // Start recall countdown if timed mode
    if (timedMode) {
      setCountdown(60);
    }

    trackEvent('items_memorized', {
      technique_name: MEMORY_METHODS[selectedMethod].name,
      practice_type: PRACTICE_TYPES[selectedMethod][selectedType].name,
      study_time_seconds: studySeconds
    });
  };

  const resetChallenge = () => {
    const practiceData = PRACTICE_TYPES[selectedMethod][selectedType];
    const randomChallenge = practiceData.items[Math.floor(Math.random() * practiceData.items.length)];
    setCurrentChallenge(randomChallenge);
    setUserInput('');
    setDetailedResults([]);

    if (inputRef.current) {
      inputRef.current.value = '';
    }

    setShowResults(false);
    setItemsHidden(false);
    setStudyTime(0);
    setStartTime(Date.now());

    if (timedMode) {
      setCountdown(30);
    } else {
      setCountdown(null);
    }
  };

  // Countdown bar component
  const CountdownBar = ({ seconds, maxSeconds }) => {
    const percentage = (seconds / maxSeconds) * 100;
    const colorClass = percentage > 50 ? 'countdown-green' : percentage > 25 ? 'countdown-yellow' : 'countdown-red';
    const isPulsing = seconds <= 5 && seconds > 0;
    return (
      <div className="countdown-bar-container mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-white font-medium text-sm">Time Remaining</span>
          <span className={`text-white font-bold text-lg ${isPulsing ? 'countdown-pulse' : ''}`}>{seconds}s</span>
        </div>
        <div className="countdown-bar-track">
          <div
            className={`countdown-bar-fill ${colorClass} ${isPulsing ? 'countdown-pulse' : ''}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  // TutorialScreen Component
  const TutorialScreen = ({ currentScreen, setCurrentScreen }) => {
    const methodKey = currentScreen.replace('_tutorial', '');
    const tutorial = TUTORIALS[methodKey];
    const method = MEMORY_METHODS[methodKey];

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="container mx-auto px-4 py-6 sm:py-12">
          <div className="text-center mb-8 sm:mb-12">
            <button
              onClick={() => setCurrentScreen(methodKey)}
              className="mb-6 px-6 py-3 bg-white/20 backdrop-blur rounded-xl text-white hover:bg-white/30 transition-all"
            >
              &larr; Back to {method.name}
            </button>
            <div className={`inline-block bg-gradient-to-br ${method.color} rounded-2xl p-5 sm:p-6 mb-6`}>
              <span className="text-4xl">{method.icon}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">{tutorial.title}</h1>
            <p className="text-base sm:text-xl text-gray-300 max-w-2xl mx-auto">{tutorial.intro}</p>
          </div>

          <div className="max-w-4xl mx-auto">
            {tutorial.steps.map((step, index) => (
              <div key={index} className="bg-white/10 backdrop-blur rounded-2xl p-5 sm:p-6 mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                  {index + 1}. {step.title}
                </h3>
                <p className="text-gray-300 mb-4">{step.description}</p>
                {step.example && (
                  <div className="bg-white/5 rounded-xl p-4 border-l-4 border-blue-500">
                    <p className="text-sm text-gray-400 mb-1 font-semibold">Example:</p>
                    <p className="text-blue-200 italic">{step.example}</p>
                  </div>
                )}
              </div>
            ))}

            <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 backdrop-blur rounded-2xl p-5 sm:p-8 mt-8">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 text-center">
                &#x1F517; Example Link
              </h3>
              <p className="text-gray-300 mb-4">
                Here's how the sequence would be linked together:
              </p>
              <div className="bg-white/10 rounded-xl p-4 sm:p-6">
                <p className="text-white font-medium mb-3">Sequence:</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {tutorial.exampleSequence.map((item, index) => (
                    <span key={index} className="px-3 py-1 bg-white/20 rounded-full text-sm text-white">
                      {index + 1}. {item}
                    </span>
                  ))}
                </div>
                <p className="text-gray-200 leading-relaxed">
                  {tutorial.exampleLink}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // MethodScreen Component
  const MethodScreen = ({ currentScreen, setCurrentScreen, progress, startPractice }) => (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto px-4 py-6 sm:py-12">
        <div className="text-center mb-8 sm:mb-12">
          <button
            onClick={() => setCurrentScreen('home')}
            className="mb-6 px-6 py-3 bg-white/20 backdrop-blur rounded-xl text-white hover:bg-white/30 transition-all"
          >
            &larr; Back to Home
          </button>
          <div className={`inline-block bg-gradient-to-br ${MEMORY_METHODS[currentScreen].color} rounded-2xl p-5 sm:p-6 mb-6`}>
            <span className="text-4xl">{MEMORY_METHODS[currentScreen].icon}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">{MEMORY_METHODS[currentScreen].name}</h1>
          <p className="text-base sm:text-xl text-gray-300 max-w-2xl mx-auto">{MEMORY_METHODS[currentScreen].description}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {Object.entries(PRACTICE_TYPES[currentScreen]).map(([key, type]) => {
            const progressKey = `${currentScreen}_${key}`;
            const typeProgress = progress[progressKey];

            return (
              <div key={key} className="bg-white/10 backdrop-blur rounded-2xl p-5 sm:p-6 hover:bg-white/20 transition-all cursor-pointer group"
                   onClick={() => startPractice(currentScreen, key)}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-white">{type.name}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    type.difficulty === 'Beginner' ? 'bg-green-500' :
                    type.difficulty === 'Intermediate' ? 'bg-yellow-500' :
                    type.difficulty === 'Advanced' ? 'bg-orange-500' : 'bg-red-500'
                  } text-white`}>
                    {type.difficulty}
                  </span>
                </div>
                <p className="text-gray-300 mb-4">{type.description}</p>
                {typeProgress && (
                  <div className="bg-white/10 rounded-lg p-3 mb-4">
                    <div className="flex justify-between text-sm text-white/80">
                      <span>Best Score: {typeProgress.bestScore}%</span>
                      <span>Attempts: {typeProgress.attempts}</span>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center text-blue-400 group-hover:text-blue-300">
                    <span>Start Practice</span>
                    <span className="ml-2 transform group-hover:translate-x-1 transition-transform">&rarr;</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentScreen(currentScreen + '_tutorial');
                    }}
                    className="text-xs text-purple-400 hover:text-purple-300 px-2 py-1 rounded hover:bg-purple-500/10 transition-colors"
                  >
                    &#x1F4D6; Tutorial
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 mb-6 max-w-5xl mx-auto flex justify-center">
          <button
            onClick={() => setCurrentScreen(currentScreen + '_tutorial')}
            className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-medium transition-all transform hover:scale-105"
          >
            &#x1F4D6; View Method Tutorial
          </button>
        </div>

        <div className="mt-10 sm:mt-16 bg-white/10 backdrop-blur rounded-2xl p-5 sm:p-8 max-w-4xl mx-auto">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">&#x1F4A1; Memory Tips</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {MEMORY_TIPS[currentScreen].map((tip, index) => (
              <div key={index} className="flex items-start space-x-3">
                <span className="text-yellow-400 text-lg">&bull;</span>
                <p className="text-gray-300">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const HomePage = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto px-4 py-6 sm:py-12">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <h1 className="text-4xl sm:text-6xl font-bold text-white mb-6">
            &#x1F9E0; Memory Master
          </h1>
          <p className="text-base sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Master Harry Lorayne's proven memory techniques through interactive practice sessions.
            Choose your method and start building extraordinary memory skills today.
          </p>

          {/* Streak Counter */}
          {streak.currentStreak > 0 && (
            <div className="streak-counter mt-6 inline-flex items-center bg-white/10 backdrop-blur rounded-full px-6 py-3">
              <span className="text-2xl mr-2">&#x1F525;</span>
              <span className="text-white font-bold text-lg">{streak.currentStreak} day streak</span>
              {streak.longestStreak > streak.currentStreak && (
                <span className="text-white/60 text-sm ml-3">(Best: {streak.longestStreak})</span>
              )}
            </div>
          )}

          {/* Badge Gallery */}
          {streak.badges && streak.badges.length > 0 && (
            <div className="badge-gallery mt-6 flex flex-wrap justify-center gap-3">
              {streak.badges.map(badgeId => {
                const badge = BADGE_DEFINITIONS[badgeId];
                if (!badge) return null;
                return (
                  <div key={badgeId} className="badge-item bg-white/10 backdrop-blur rounded-xl px-4 py-2 flex items-center space-x-2" title={badge.description}>
                    <span className="text-xl">{badge.icon}</span>
                    <span className="text-white text-sm font-medium">{badge.name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Method Selection Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 max-w-7xl mx-auto">
          {Object.entries(MEMORY_METHODS).map(([key, method]) => (
            <div key={key} className="group cursor-pointer" onClick={() => setCurrentScreen(key)}>
              <div className={`bg-gradient-to-br ${method.color} rounded-2xl p-5 sm:p-6 shadow-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-3xl h-full`}>
                <div className="text-center h-full flex flex-col justify-between">
                  <div>
                    <div className="text-4xl mb-4">{method.icon}</div>
                    <h2 className="text-xl font-bold text-white mb-3">{method.name}</h2>
                    <p className="text-sm text-white/90 mb-4">{method.description}</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur rounded-xl p-3 mb-2">
                    <p className="text-white font-medium text-sm">Click to practice</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentScreen(key + '_tutorial');
                    }}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                  >
                    &#x1F4D6; Tutorial
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        {Object.keys(progress).length > 0 && (
          <div className="mt-10 sm:mt-16 bg-white/10 backdrop-blur rounded-2xl p-5 sm:p-8 max-w-6xl mx-auto">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">Your Progress</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {Object.entries(progress).map(([key, data]) => {
                const [method, type] = key.split('_');
                const methodName = MEMORY_METHODS[method]?.name || method;
                const typeName = PRACTICE_TYPES[method]?.[type]?.name || type;
                return (
                  <div key={key} className="bg-white/10 rounded-xl p-3 text-center">
                    <p className="text-white/80 text-xs mb-1">{methodName}</p>
                    <p className="text-white font-medium text-sm">{typeName}</p>
                    <p className="text-yellow-400 text-lg font-bold">{data.bestScore}%</p>
                    <p className="text-white/60 text-xs">{data.attempts} attempts</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const PracticeScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto px-4 py-6 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => setCurrentScreen(selectedMethod)}
            className="mb-6 px-6 py-3 bg-white/20 backdrop-blur rounded-xl text-white hover:bg-white/30 transition-all"
          >
            &larr; Back to {MEMORY_METHODS[selectedMethod].name}
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            {PRACTICE_TYPES[selectedMethod][selectedType].name}
          </h1>
          <p className="text-gray-300">
            {PRACTICE_TYPES[selectedMethod][selectedType].description}
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {!showResults ? (
            <>
              {/* Challenge Display */}
              {!itemsHidden && (
                <div className="bg-white/10 backdrop-blur rounded-2xl p-5 sm:p-8 mb-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">Memorize These Items:</h2>

                  {/* Countdown bar for study phase */}
                  {timedMode && countdown !== null && (
                    <CountdownBar seconds={countdown} maxSeconds={30} />
                  )}

                  <div className="grid grid-cols-1 gap-4 mb-6">
                    {currentChallenge.map((item, index) => (
                      <div key={index} className="bg-white/10 rounded-xl p-4 text-center">
                        <span className="text-lg text-white font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-center space-y-4">
                    {/* Timed mode toggle */}
                    <div className="flex items-center justify-center space-x-3">
                      <span className="text-white text-sm">Timed Challenge</span>
                      <button
                        onClick={() => setTimedMode(!timedMode)}
                        className={`timed-toggle relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${timedMode ? 'bg-orange-500' : 'bg-white/20'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${timedMode ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      {timedMode && <span className="text-orange-400 text-sm font-medium">&#x23F1;&#xFE0F; ON</span>}
                    </div>
                    <button
                      onClick={hideItemsAndStartTyping}
                      className="w-full sm:w-auto px-6 sm:px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white rounded-xl font-medium transition-all transform hover:scale-105 text-base sm:text-lg"
                    >
                      &#x1F4DD; I've Memorized These - Let Me Type!
                    </button>
                  </div>
                </div>
              )}

              {/* Input Section */}
              {itemsHidden && (
                <div className="bg-white/10 backdrop-blur rounded-2xl p-5 sm:p-8" id="input-section">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-4">Enter what you remember (comma-separated):</h3>

                  {/* Countdown bar for recall phase */}
                  {timedMode && countdown !== null && (
                    <CountdownBar seconds={countdown} maxSeconds={60} />
                  )}

                  {studyTime > 0 && (
                    <p className="text-gray-300 mb-4">Study time: {studyTime} seconds</p>
                  )}
                  <UncontrolledTextarea
                    ref={inputRef}
                    className="w-full h-32 p-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Type your answers here, separated by commas..."
                  />
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mt-6">
                    <button
                      onClick={resetChallenge}
                      className="w-full sm:w-auto px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors"
                    >
                      &#x1F504; New Challenge
                    </button>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <button
                        onClick={() => setItemsHidden(false)}
                        className="w-full sm:w-auto px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl transition-colors"
                      >
                        &#x1F441;&#xFE0F; Show Items Again
                      </button>
                      <button
                        onClick={submitAnswer}
                        className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-medium transition-all transform hover:scale-105"
                      >
                        &#x2705; Submit Answer
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Results Display */
            <div className="bg-white/10 backdrop-blur rounded-2xl p-5 sm:p-8 text-center">
              <div className="text-5xl sm:text-6xl mb-4">
                {score >= 80 ? '\u{1F389}' : score >= 60 ? '\u{1F44D}' : '\u{1F4AA}'}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Score: {score}%
              </h2>
              <p className="text-gray-300 mb-2">Study time: {studyTime} seconds | Answer time: {timeSpent} seconds</p>
              <p className="text-gray-300 mb-8">
                You got {detailedResults.filter(r => r.isCorrect).length} out of {currentChallenge.length} correct
              </p>

              {/* Detailed Results with highlighting */}
              <div className="space-y-3 mb-8 text-left">
                {detailedResults.map((result, index) => (
                  <div key={index} className={`result-item rounded-xl p-4 flex items-start justify-between ${result.isCorrect ? 'result-correct' : 'result-incorrect'}`}>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-lg">{result.isCorrect ? '\u2705' : '\u274C'}</span>
                        <span className="text-white font-medium">{result.expected}</span>
                      </div>
                      {!result.isCorrect && result.userAnswer && (
                        <p className="text-white/60 text-sm ml-8">Your answer: {result.userAnswer}</p>
                      )}
                      {!result.isCorrect && !result.userAnswer && (
                        <p className="text-white/40 text-sm italic ml-8">(no answer)</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-center gap-3 sm:gap-4">
                <button
                  onClick={resetChallenge}
                  className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white rounded-xl font-medium transition-all transform hover:scale-105"
                >
                  Try Again
                </button>
                <button
                  onClick={() => setCurrentScreen(selectedMethod)}
                  className="w-full sm:w-auto px-8 py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl font-medium transition-all"
                >
                  Choose Different Practice
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div className="toast-notification">
          <span className="text-2xl mr-2">{toast.icon}</span>
          <span className="text-white font-medium">{toast.message}</span>
        </div>
      )}
    </div>
  );

  // Render appropriate screen
  if (currentScreen === "home") return <HomePage />;
  if (currentScreen === "practice") return <PracticeScreen />;
  if (currentScreen.endsWith("_tutorial")) return <TutorialScreen currentScreen={currentScreen} setCurrentScreen={setCurrentScreen} />;
  return <MethodScreen currentScreen={currentScreen} setCurrentScreen={setCurrentScreen} progress={progress} startPractice={startPractice} />;
}

export default App;
